/* ═══════════════════════════════════════════════════════════════
   DirectoryTab — Members + Teams merged (docs/PEOPLE_REBUILD_PLAN.md
   §5, extended by the Team Management UX Overhaul, 2026-08-18).
   Master-detail form: team tree (far left, toggleable) + people
   DataTable (center) + a SINGLE right-side panel slot (PersonPanel /
   TeamEditorPanel / AddMemberPanel — mutually exclusive, "side
   panels are THE pattern for People"). Team CRUD and the "Add
   Member" top bar both moved into that panel slot; the team tree
   replaces the old Teams-modal's team list, with drag-and-drop:
   drop a person row on a team to assign, drag a team onto another
   to nest, drag a team to the tree's root area to un-nest.
   ═══════════════════════════════════════════════════════════════ */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Star, Users, UserX, UsersRound, Eye, EyeOff } from 'lucide-react';
import { DataTable, type Column, type GroupOption } from '../../components/DataTable';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import { toAddress } from '../../routing/address';
import { StatusBadge } from '../../components/StatusBadge';
import { Prefs } from '../../services/prefsService';
import type {
  TeamMember,
  Team,
  TeamAssignment,
  Role,
  BillingRole,
  Project,
  ProjectAssignment,
  Invitation,
} from '../../../shared/types';
import type { Department } from '../../../shared/entities';
import { PendingInvitations } from './PendingInvitations';
import { AddMemberPanel } from './AddMemberPanel';
import { TeamEditorPanel } from './TeamEditorPanel';
import { PersonPanel } from './PersonPanel';
import { TeamTree, MEMBER_DRAG_TYPE } from './TeamTree';
import { TableSkeleton, StaleDataBanner } from './kit';
import { displayName, nameSortKey } from './styles';

// Per-user "Show archived" toggle persistence — same additive
// localStorage-seed + server-wins-once pattern as DataTable's `dt_*`
// keys and the org chart's `orgchart_*` prefs (Time & Expense rebuild,
// Tranche 6 / D11). Never rename — additive only.
const SHOW_ARCHIVED_PREF_KEY = 'directory_show_archived';
const SHOW_ARCHIVED_LS_KEY = 'specforma:directory:showArchived';

export type DirectoryActivePanel = 'person' | 'team' | 'addMember' | null;

export interface DirectoryTabProps {
  loading: boolean;
  stale: boolean;
  onRetry: () => void;

  // Invitations + add-member panel
  invitations: Invitation[];
  onCancelInvitation: (id: string) => void;
  activePanel: DirectoryActivePanel;
  showAddMemberPanel: boolean;
  inviteEmail: string;
  inviteFirstName: string;
  inviteMiddleName: string;
  inviteLastName: string;
  inviteRoleId: string;
  inviteBillingRoleId: string;
  setInviteEmail: (v: string) => void;
  setInviteFirstName: (v: string) => void;
  setInviteMiddleName: (v: string) => void;
  setInviteLastName: (v: string) => void;
  setInviteRoleId: (v: string) => void;
  setInviteBillingRoleId: (v: string) => void;
  onCloseAddMemberPanel: () => void;
  addMemberDirect: () => void;
  inviteMember: () => void;

  // Table data
  members: TeamMember[];
  teams: Team[];
  teamAssignments: TeamAssignment[];
  roles: Role[];
  billingRoles: BillingRole[];
  departments: Department[];
  allAssignments: ProjectAssignment[];
  roleName: (m: TeamMember) => string;
  teamName: (id: string) => string;
  canViewRoles: boolean;
  canViewBilling: boolean;
  canEditMembers: boolean;
  canRemoveMembers: boolean;
  canHardDeleteMembers: boolean;
  canManageRoles: boolean;
  // Standard DataTable bulk bar (docs/FORECASTING_V2_TABLE_STANDARD_PLAN.md)
  permissions?: string[];
  onBulkArchive: (items: TeamMember[]) => void | Promise<void>;
  onBulkUndoArchive: (items: TeamMember[]) => void | Promise<void>;
  onBulkHardDelete: (items: TeamMember[]) => void | Promise<void>;

  // Row selection / master-detail
  selectedMember: TeamMember | null;
  onRowClick: (m: TeamMember) => void;

  // PersonPanel passthrough
  memberDirty: boolean;
  memberProjectAssignments: ProjectAssignment[];
  memberTeamAssignments: TeamAssignment[];
  availableProjects: Project[];
  availableTeamsForMember: Team[];
  addingProject: boolean;
  projects: Project[];
  canSeeRates: boolean;
  canHardDeleteMember: boolean;
  onUpdateMember: (updates: Partial<TeamMember>) => void;
  onSaveMember: () => void;
  onDeactivateMember: (id: string) => void;
  onReactivateMember: (id: string) => void;
  onHardDeleteMember: (id: string) => void;
  onClosePanel: () => void;
  onAssignProject: (projectId: string) => void;
  onUnassignProject: (assignmentId: string) => void;
  onToggleAddProject: () => void;
  onAddTeamToMember: (teamId: string) => void;
  onRemoveTeamFromMember: (teamId: string) => void;
  onToggleMemberTeamRole: (teamId: string) => void;

  // Team tree + TeamEditorPanel passthrough
  memberName: (id?: string) => string;
  editingTeam: Team | null;
  teamDirty: boolean;
  editingTeamAssignments: TeamAssignment[];
  availableTeamMembers: TeamMember[];
  addingTeamMember: boolean;
  onSelectTeam: (t: Team) => void;
  onUpdateTeam: (updates: Partial<Team>) => void;
  onSaveTeam: () => void;
  onDeleteTeam: (id: string) => void;
  onAddMemberToTeam: (memberId: string, role: 'leader' | 'member') => void;
  onRemoveMemberFromTeam: (assignmentId: string) => void;
  onChangeTeamMemberRole: (assignmentId: string, newRole: 'leader' | 'member') => void;
  onToggleAddTeamMember: () => void;
  onCloseTeamPanel: () => void;
  onDropMemberOnTeam: (memberId: string, teamId: string) => void;
  onReparentTeam: (teamId: string, newParentId: string | undefined) => void;
}

export function DirectoryTab({
  loading,
  stale,
  onRetry,
  invitations,
  onCancelInvitation,
  activePanel,
  showAddMemberPanel,
  inviteEmail,
  inviteFirstName,
  inviteMiddleName,
  inviteLastName,
  inviteRoleId,
  inviteBillingRoleId,
  setInviteEmail,
  setInviteFirstName,
  setInviteMiddleName,
  setInviteLastName,
  setInviteRoleId,
  setInviteBillingRoleId,
  onCloseAddMemberPanel,
  addMemberDirect,
  inviteMember,
  members,
  teams,
  teamAssignments,
  roles,
  billingRoles,
  departments,
  allAssignments,
  roleName,
  teamName,
  canViewRoles,
  canViewBilling,
  canEditMembers,
  canRemoveMembers,
  canHardDeleteMembers,
  canManageRoles,
  permissions,
  onBulkArchive,
  onBulkUndoArchive,
  onBulkHardDelete,
  selectedMember,
  onRowClick,
  memberDirty,
  memberProjectAssignments,
  memberTeamAssignments,
  availableProjects,
  availableTeamsForMember,
  addingProject,
  projects,
  canSeeRates,
  canHardDeleteMember,
  onUpdateMember,
  onSaveMember,
  onDeactivateMember,
  onReactivateMember,
  onHardDeleteMember,
  onClosePanel,
  onAssignProject,
  onUnassignProject,
  onToggleAddProject,
  onAddTeamToMember,
  onRemoveTeamFromMember,
  onToggleMemberTeamRole,
  memberName,
  editingTeam,
  teamDirty,
  editingTeamAssignments,
  availableTeamMembers,
  addingTeamMember,
  onSelectTeam,
  onUpdateTeam,
  onSaveTeam,
  onDeleteTeam,
  onAddMemberToTeam,
  onRemoveMemberFromTeam,
  onChangeTeamMemberRole,
  onToggleAddTeamMember,
  onCloseTeamPanel,
  onDropMemberOnTeam,
  onReparentTeam,
}: DirectoryTabProps) {
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [showTeamTree, setShowTeamTree] = useState(false);

  // "Show archived" — Directory default-hides archived (inactive) people;
  // this toggle reproduces today's always-show view. Seeded from
  // localStorage for instant first paint, then reconciled against the
  // signed-in user's cloud pref once `Prefs` finishes loading.
  const [showArchived, setShowArchivedState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SHOW_ARCHIVED_LS_KEY) === 'true';
  });
  function setShowArchived(v: boolean) {
    setShowArchivedState(v);
    try {
      window.localStorage.setItem(SHOW_ARCHIVED_LS_KEY, v ? 'true' : 'false');
    } catch {
      /* best-effort */
    }
    Prefs.set(SHOW_ARCHIVED_PREF_KEY, v);
  }
  const prefsAppliedRef = useRef(false);
  const [, forceTick] = useState(0);
  useEffect(() => Prefs.subscribe(() => forceTick((t) => t + 1)), []);
  useEffect(() => {
    if (prefsAppliedRef.current || !Prefs.isReady()) return;
    prefsAppliedRef.current = true;
    const v = Prefs.get(SHOW_ARCHIVED_PREF_KEY);
    if (typeof v === 'boolean') setShowArchivedState(v);
  });

  function getMemberTeamAssignments(memberId: string): TeamAssignment[] {
    return teamAssignments.filter((ta) => ta.teamMemberId === memberId);
  }
  function getProjectAssignmentCount(memberId: string): number {
    return allAssignments.filter((a) => a.teamMemberId === memberId).length;
  }

  const unassignedIds = useMemo(
    () =>
      new Set(members.filter((m) => getMemberTeamAssignments(m.id).length === 0).map((m) => m.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [members, teamAssignments]
  );

  const archivedCount = useMemo(() => members.filter((m) => !m.active).length, [members]);

  // Toggle OFF (default) = archived people hidden. Toggle ON reproduces
  // today's unfiltered view exactly (archived people were always shown
  // before this tranche — nothing else in the Directory changes).
  const visibilityFiltered = showArchived ? members : members.filter((m) => m.active);
  const tableData = showUnassignedOnly
    ? visibilityFiltered.filter((m) => unassignedIds.has(m.id))
    : visibilityFiltered;

  const memberColumns: Column<TeamMember>[] = [
    {
      key: 'name',
      label: 'Name',
      width: 180,
      render: (m) => <span style={{ fontWeight: 600 }}>{displayName(m)}</span>,
      // Sorts by last name then first — the point of the name split
      // (docs/PEOPLE_REBUILD_PLAN.md §5).
      getValue: (m) => nameSortKey(m),
    },
    // Optional split-name columns — the combined Name column above stays
    // the default view; these live behind the column picker (checkbox off
    // by default) for firms that want to sort/scan by name part directly.
    {
      key: 'firstName',
      label: 'First',
      width: 120,
      defaultHidden: true,
      render: (m) => (
        <span>{m.firstName || <span style={{ color: 'var(--tm)' }}>&ndash;</span>}</span>
      ),
      getValue: (m) => m.firstName || '',
    },
    {
      key: 'middleName',
      label: 'Middle',
      width: 120,
      defaultHidden: true,
      render: (m) => (
        <span>{m.middleName || <span style={{ color: 'var(--tm)' }}>&ndash;</span>}</span>
      ),
      getValue: (m) => m.middleName || '',
    },
    {
      key: 'lastName',
      label: 'Last',
      width: 120,
      defaultHidden: true,
      render: (m) => (
        <span>{m.lastName || <span style={{ color: 'var(--tm)' }}>&ndash;</span>}</span>
      ),
      getValue: (m) => m.lastName || '',
    },
    {
      key: 'email',
      label: 'Email',
      width: 200,
      render: (m) => (
        <span
          style={{ color: m.email ? 'var(--tx)' : 'var(--tm)', fontSize: 'var(--font-size-base)' }}
        >
          {m.email || '–'}
        </span>
      ),
      getValue: (m) => m.email || '',
    },
    {
      key: 'roleId',
      label: 'Privilege Role',
      width: 140,
      render: (m) => {
        const rn = roleName(m);
        return rn ? (
          <span
            style={{
              padding: `${rem(2)} ${rem(8)}`,
              borderRadius: 'var(--radius-md)',
              background: 'var(--sa)',
              color: 'var(--tx)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              border: '1px solid var(--bd)',
              whiteSpace: 'nowrap',
            }}
          >
            {rn}
          </span>
        ) : (
          <span style={{ color: 'var(--tm)' }}>&ndash;</span>
        );
      },
      getValue: (m) => roleName(m),
    },
    {
      key: 'billingRoleId',
      label: 'Title',
      width: 130,
      render: (m) => {
        const br = m.billingRoleId ? billingRoles.find((r) => r.id === m.billingRoleId) : null;
        return br ? (
          <span
            style={{
              padding: `${rem(2)} ${rem(8)}`,
              borderRadius: 'var(--radius-md)',
              background: 'rgba(var(--acr),0.08)',
              color: 'var(--ac)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              border: '1px solid rgba(var(--acr),0.2)',
              whiteSpace: 'nowrap',
            }}
          >
            {br.name}
          </span>
        ) : (
          <span style={{ color: 'var(--tm)' }}>&ndash;</span>
        );
      },
      getValue: (m) => {
        const br = m.billingRoleId ? billingRoles.find((r) => r.id === m.billingRoleId) : null;
        return br?.name || '';
      },
    },
    {
      key: 'teams',
      label: 'Teams',
      width: 200,
      sortable: false,
      render: (m) => {
        const mts = getMemberTeamAssignments(m.id);
        if (mts.length === 0) return <span style={{ color: 'var(--tm)' }}>&ndash;</span>;
        return (
          <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
            {mts.map((ta) => {
              const team = teams.find((t) => t.id === ta.teamId);
              const tc = team?.color;
              return (
                <span
                  key={ta.id}
                  style={{
                    padding: `${rem(1)} ${rem(8)}`,
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    background: tc
                      ? tc + '18'
                      : ta.teamRole === 'leader'
                        ? 'rgba(var(--acr),0.15)'
                        : 'var(--sa)',
                    color: tc || (ta.teamRole === 'leader' ? 'var(--ac)' : 'var(--tm)'),
                    border: `1px solid ${tc ? tc + '40' : ta.teamRole === 'leader' ? 'rgba(var(--acr),0.3)' : 'var(--bd)'}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: rem(3),
                    whiteSpace: 'nowrap',
                  }}
                >
                  {teamName(ta.teamId)}
                  {ta.teamRole === 'leader' && (
                    <Icon icon={Star} em={0.9091} fill="currentColor" aria-hidden="true" />
                  )}
                </span>
              );
            })}
          </div>
        );
      },
      getValue: (m) =>
        getMemberTeamAssignments(m.id)
          .map((ta) => teamName(ta.teamId))
          .join(', '),
    },
    {
      key: 'projects',
      label: 'Projects',
      width: 80,
      render: (m) => {
        const count = getProjectAssignmentCount(m.id);
        return count > 0 ? (
          <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--ac)' }}>
            {count}
          </span>
        ) : (
          <span style={{ color: 'var(--tm)' }}>&ndash;</span>
        );
      },
      getValue: (m) => getProjectAssignmentCount(m.id),
    },
    {
      key: 'active',
      label: 'Status',
      width: 90,
      render: (m) => <StatusBadge status={m.active ? 'active' : 'archived'} />,
      getValue: (m) => (m.active ? 'Active' : 'Archived'),
    },
  ];

  const visibleMemberColumns = useMemo(
    () =>
      memberColumns.filter((col) => {
        if (col.key === 'roleId' && !canViewRoles) return false;
        if (col.key === 'billingRoleId' && !canViewBilling) return false;
        return true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canViewRoles, canViewBilling, roles, billingRoles, teams, teamAssignments, allAssignments]
  );

  // Each keyed to the COLUMN it replaces the default grouping for
  // (DataTable merges by key) — these used to be keyed 'role'/'team'/
  // 'status', which matched no column ('roleId'/'teams'/'active' do) and
  // were silently dropped from the group menu.
  const memberGroupOptions: GroupOption<TeamMember>[] = [
    { key: 'roleId', label: 'Role', getValue: (m) => roleName(m) || 'No Role' },
    {
      key: 'teams',
      label: 'Team',
      getValue: (m) => {
        const mts = getMemberTeamAssignments(m.id);
        return mts.length > 0 ? teamName(mts[0].teamId) : 'No Team';
      },
    },
    { key: 'active', label: 'Status', getValue: (m) => (m.active ? 'Active' : 'Archived') },
  ];

  const toolbarExtra = (
    <>
      <Button
        variant={showUnassignedOnly ? 'secondary' : 'outline'}
        size="sm"
        className="gap-1.5"
        aria-pressed={showUnassignedOnly}
        onClick={() => setShowUnassignedOnly((v) => !v)}
        title="Filter to members not on any team"
      >
        <Icon icon={UserX} em={1.4} aria-hidden="true" />
        Not on a team ({unassignedIds.size})
      </Button>
      <Button
        variant={showArchived ? 'secondary' : 'outline'}
        size="sm"
        className="gap-1.5"
        aria-pressed={showArchived}
        onClick={() => setShowArchived(!showArchived)}
        title={showArchived ? 'Hide archived people' : 'Show archived people'}
      >
        {showArchived ? (
          <Icon icon={EyeOff} em={1.4} aria-hidden="true" />
        ) : (
          <Icon icon={Eye} em={1.4} aria-hidden="true" />
        )}
        Show archived ({archivedCount})
      </Button>
      <Button
        variant={showTeamTree ? 'secondary' : 'outline'}
        size="sm"
        className="gap-1.5"
        aria-pressed={showTeamTree}
        onClick={() => setShowTeamTree((v) => !v)}
        title="Show/hide the team tree"
      >
        <Icon icon={UsersRound} em={1.4} aria-hidden="true" />
        Teams ({teams.length})
      </Button>
    </>
  );

  const rightPanel =
    activePanel === 'person' && selectedMember ? (
      <PersonPanel
        member={selectedMember}
        dirty={memberDirty}
        roles={roles}
        billingRoles={billingRoles}
        departments={departments}
        teams={teams}
        canSeeRates={canSeeRates}
        canManageRoles={canManageRoles}
        canRemove={canRemoveMembers}
        canHardDelete={canHardDeleteMember}
        canEditTeams={canEditMembers}
        projects={projects}
        memberTeamAssignments={memberTeamAssignments}
        memberProjectAssignments={memberProjectAssignments}
        availableProjects={availableProjects}
        availableTeams={availableTeamsForMember}
        addingProject={addingProject}
        onUpdate={onUpdateMember}
        onSave={onSaveMember}
        onDeactivate={onDeactivateMember}
        onReactivate={onReactivateMember}
        onHardDelete={onHardDeleteMember}
        onClose={onClosePanel}
        onAssignProject={onAssignProject}
        onUnassignProject={onUnassignProject}
        onToggleAddProject={onToggleAddProject}
        onAddTeam={onAddTeamToMember}
        onRemoveTeam={onRemoveTeamFromMember}
        onToggleTeamRole={onToggleMemberTeamRole}
      />
    ) : activePanel === 'team' && editingTeam ? (
      <TeamEditorPanel
        teams={teams}
        members={members}
        memberName={memberName}
        canEdit={canEditMembers}
        editingTeam={editingTeam}
        teamDirty={teamDirty}
        editingTeamAssignments={editingTeamAssignments}
        availableMembers={availableTeamMembers}
        addingMember={addingTeamMember}
        onUpdate={onUpdateTeam}
        onSave={onSaveTeam}
        onDelete={onDeleteTeam}
        onAddMember={onAddMemberToTeam}
        onRemoveMember={onRemoveMemberFromTeam}
        onChangeRole={onChangeTeamMemberRole}
        onToggleAddMember={onToggleAddTeamMember}
        onClose={onCloseTeamPanel}
      />
    ) : activePanel === 'addMember' && showAddMemberPanel ? (
      <AddMemberPanel
        roles={roles}
        billingRoles={billingRoles}
        inviteEmail={inviteEmail}
        inviteFirstName={inviteFirstName}
        inviteMiddleName={inviteMiddleName}
        inviteLastName={inviteLastName}
        inviteRoleId={inviteRoleId}
        inviteBillingRoleId={inviteBillingRoleId}
        canManageRoles={canManageRoles}
        setInviteEmail={setInviteEmail}
        setInviteFirstName={setInviteFirstName}
        setInviteMiddleName={setInviteMiddleName}
        setInviteLastName={setInviteLastName}
        setInviteRoleId={setInviteRoleId}
        setInviteBillingRoleId={setInviteBillingRoleId}
        onClose={onCloseAddMemberPanel}
        addMemberDirect={addMemberDirect}
        inviteMember={inviteMember}
      />
    ) : null;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {stale && <StaleDataBanner onRetry={onRetry} />}

      <PendingInvitations invitations={invitations} roles={roles} onCancel={onCancelInvitation} />

      {loading ? (
        <TableSkeleton />
      ) : (
        <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
          {showTeamTree && (
            <TeamTree
              teams={teams}
              teamAssignments={teamAssignments}
              selectedTeamId={activePanel === 'team' ? editingTeam?.id : null}
              canEdit={canEditMembers}
              onSelectTeam={onSelectTeam}
              onDropMemberOnTeam={onDropMemberOnTeam}
              onReparentTeam={onReparentTeam}
            />
          )}

          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <DataTable<TeamMember>
              columns={visibleMemberColumns}
              data={tableData}
              keyField="id"
              storageKey="team-members"
              onRowClick={canEditMembers ? onRowClick : undefined}
              rowHref={
                canEditMembers ? (m) => toAddress({ view: 'team', memberId: m.id }) : undefined
              }
              onRowDragStart={
                canEditMembers
                  ? (m, e) => {
                      e.dataTransfer.setData(MEMBER_DRAG_TYPE, m.id);
                      e.dataTransfer.effectAllowed = 'copy';
                    }
                  : undefined
              }
              groupOptions={memberGroupOptions}
              toolbarExtra={toolbarExtra}
              permissions={permissions}
              bulkActions={{
                archive: canRemoveMembers
                  ? {
                      permission: 'team_members.deactivate',
                      onArchive: onBulkArchive,
                      onUndo: onBulkUndoArchive,
                      label: 'Archive',
                    }
                  : undefined,
                delete: canHardDeleteMembers
                  ? {
                      permission: 'admin_center.manage_roles',
                      onDelete: onBulkHardDelete,
                      confirmTitle: 'Delete team members permanently?',
                      confirmDescription: () =>
                        'This permanently deletes their records and logins — it cannot be undone. To just remove access and keep history, archive instead.',
                    }
                  : undefined,
                exportCsv: { permission: 'team_members.export' },
              }}
              emptyIcon={<Icon icon={Users} em={0.6} aria-hidden="true" />}
              emptyTitle="No Team Members Yet"
              emptyMessage="Invite your first team member to get started."
              initialSort={{ key: 'name', direction: 'asc' }}
            />
          </div>

          {rightPanel}
        </div>
      )}
    </div>
  );
}
